import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { useLocale } from "@/context/LocaleContext";
import { PageHeader } from "@/components/common";
import api from "@/lib/api";
import {
  CalendarCheck, Lightbulb, Linkedin, Facebook, Mail, Newspaper, Megaphone,
  Magnet, Feather, Brain, FolderOpen, Lock, ArrowRight, MessageSquareQuote, Sparkles, X,
} from "lucide-react";
import { toast } from "sonner";
import { IdeaMailDashboardCard } from "@/components/WeeklyIdeasOptIn";

const PLAN_RANK = { gratis: 0, start: 1, pro: 2, business: 2 };
const PLAN_LABELS = { gratis: "Gratis", start: "Start", pro: "Pro", business: "Business" };

const ITEMS = [
  { to: "/idebank", label: "Idégenerator", icon: Lightbulb, desc: "10 skræddersyede idéer til hver kanal." },
  { to: "/linkedin", label: "LinkedIn-opslag", icon: Linkedin, desc: "Færdige opslag, klar til at poste." },
  { to: "/facebook", label: "Facebook-opslag", icon: Facebook, desc: "Færdige opslag, tilpasset Facebook." },
  { to: "/debatindlaeg", label: "Debatindlæg", icon: MessageSquareQuote, desc: "Skarpe, overbevisende debatindlæg der sætter dagsordenen.", min: "start" },
  { to: "/nyhedsbrev", label: "Nyhedsbrev", icon: Mail, desc: "Emne, overskrift og brødtekst til nyhedsbrevet.", min: "start" },
  { to: "/presse", label: "Presse", icon: Newspaper, desc: "Vurdering af historien + færdig pressemeddelelse.", min: "start" },
  { to: "/kampagner", label: "Kampagner", icon: Megaphone, desc: "Færdige kampagnetekster til annoncer og landingsider.", min: "pro" },
  { to: "/leadgenerator", label: "Leadgenerator", icon: Magnet, desc: "Komplette lead magnets og guides — smukt brandet.", min: "pro" },
  { to: "/brandfortaelling", label: "Brandfortælling", icon: Feather, desc: "Jeres historie fortalt skarpt.", min: "pro" },
  { to: "/ugens-forslag", label: "Ugens forslag", icon: CalendarCheck, desc: "Ugentlige forslag til hvad I bør kommunikere om." },
  { to: "/vidensbank", label: "Vidensbank", icon: Brain, desc: "Upload dokumenter — så Aicom lærer jer endnu bedre at kende.", min: "start" },
  { to: "/projekter", label: "Gemte projekter", icon: FolderOpen, desc: "Alle jeres gemte udkast ét sted." },
];

function greeting() {
  const h = new Date().getHours();
  if (h < 10) return "Godmorgen";
  if (h < 18) return "Goddag";
  return "Godaften";
}

export default function Dashboard() {
  const { user, company, refresh } = useAuth();
  const { t } = useLocale();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [showWelcome, setShowWelcome] = useState(false);

  useEffect(() => {
    if (params.get("welcome") !== "1") return;
    setShowWelcome(true);
    // Finalize trial subscription if returning from a Stripe redirect (e.g. 3D Secure).
    const sub = params.get("sub");
    const si = params.get("redirect_status") === "succeeded" ? params.get("setup_intent") : null;
    if (si) api.post("/billing/confirm-trial", { setup_intent_id: si }).then(() => refresh()).catch(() => {});
    if (sub && params.get("redirect_status") === "succeeded") {
      api.post("/billing/confirm-trial", { subscription_id: sub }).then(() => refresh()).catch(() => {});
    }
    const next = new URLSearchParams(params);
    ["welcome", "sub", "redirect_status", "setup_intent", "setup_intent_client_secret"].forEach((k) => next.delete(k));
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const go = (item, locked) => {
    if (locked) {
      toast.info(t('Opgradér til {plan} for at låse "{label}" op', { plan: t(PLAN_LABELS[item.min]), label: t(item.label) }));
      navigate("/priser");
      return;
    }
    navigate(item.to);
  };

  return (
    <div>
      {showWelcome && (
        <motion.div
          initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
          className="mb-6 rounded-xl border p-4 flex items-start gap-3 relative"
          style={{ borderColor: "#1dddbf", backgroundColor: "#1dddbf1a" }}
          data-testid="dashboard-welcome"
        >
          <Sparkles className="h-5 w-5 shrink-0 mt-0.5" style={{ color: "#1dddbf" }} />
          <div className="pr-6">
            <p className="font-medium text-sm">{t("Velkommen til Aicom AI!")}</p>
            <p className="text-sm text-muted-foreground mt-0.5">{t("Jeres virksomhedsprofil er klar. Vælg et modul nedenfor for at lave jeres første indhold.")}</p>
          </div>
          <button onClick={() => setShowWelcome(false)} className="absolute top-3 right-3 text-muted-foreground hover:text-foreground" data-testid="dashboard-welcome-close">
            <X className="h-4 w-4" />
          </button>
        </motion.div>
      )}
      <PageHeader
        overline="Dashboard"
        title={`${t(greeting())}, ${(user?.name || "").trim().split(" ")[0] || t("der")} 👋`}
        subtitle="Vælg en funktion for at komme i gang med jeres kommunikation."
      />

      <IdeaMailDashboardCard />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="dashboard-overview">
        {ITEMS.map((item, i) => {
          const locked = user?.role !== "admin" && item.min && PLAN_RANK[user?.plan || "gratis"] < PLAN_RANK[item.min];
          return (
            <motion.button
              key={item.to}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: (i % 3) * 0.06 }}
              onClick={() => go(item, locked)}
              data-testid={`dashboard-card-${item.to.replace("/", "")}`}
              className={`group text-left rounded-xl border border-border bg-card p-6 relative transition-colors duration-200 hover:border-muted-foreground/50`}
            >
              <div className="flex items-start justify-between">
                <item.icon className="h-6 w-6 text-foreground" strokeWidth={1.5} />
                {locked ? (
                  <Lock className="h-4 w-4 text-muted-foreground" strokeWidth={2} />
                ) : (
                  <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                )}
              </div>
              <h3 className="font-display text-lg font-medium mt-4">{t(item.label)}</h3>
              <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{t(item.desc)}</p>
              {locked && (
                <span className="inline-block mt-3 text-xs text-muted-foreground">{t("Kræver {plan} — opgradér", { plan: t(PLAN_LABELS[item.min]) })}</span>
              )}
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}
